import React, { useRef, useState, useEffect } from 'react';
import html2canvas from 'html2canvas';
import { Button } from './ui/button';
import { Download, Loader2 } from 'lucide-react';

// Official Achraj Logo
const ACHRAJ_LOGO = "/achraj_logo.png";

// Mask phone number - hide last 4 digits
const maskPhoneNumber = (phone) => {
  if (!phone) return 'N/A';
  const phoneStr = phone.toString().replace(/\D/g, '');
  if (phoneStr.length <= 4) return '****';
  return phoneStr.slice(0, -4) + '****';
};

// Generate circuit pattern for futuristic look
const CircuitPattern = () => (
  <svg width="100%" height="100%" style={{ position: 'absolute', top: 0, left: 0, opacity: 0.1, pointerEvents: 'none' }}>
    <defs>
      <pattern id="circuit" x="0" y="0" width="60" height="60" patternUnits="userSpaceOnUse">
        <path d="M30 0 L30 15 M30 45 L30 60 M0 30 L15 30 M45 30 L60 30" stroke="#00F5FF" strokeWidth="1" fill="none"/>
        <circle cx="30" cy="30" r="3" fill="#00F5FF"/>
        <circle cx="30" cy="15" r="2" fill="#00F5FF"/>
        <circle cx="30" cy="45" r="2" fill="#00F5FF"/>
        <circle cx="15" cy="30" r="2" fill="#00F5FF"/>
        <circle cx="45" cy="30" r="2" fill="#00F5FF"/>
      </pattern>
    </defs>
    <rect width="100%" height="100%" fill="url(#circuit)"/>
  </svg>
);

// Animated data stream bars
const DataStream = ({ count = 20 }) => (
  <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '25px' }}>
    {Array.from({ length: count }).map((_, i) => {
      const height = 8 + Math.sin(i * 0.5) * 12 + Math.random() * 5;
      return (
        <div
          key={i}
          style={{
            width: '3px',
            height: `${height}px`,
            background: `linear-gradient(180deg, #00F5FF ${100 - height * 2}%, #D4AF37 100%)`,
            borderRadius: '1px',
            opacity: 0.8 + Math.random() * 0.2
          }}
        />
      );
    })}
  </div>
);

const MemberIDCard = ({ member, onClose, showCloseButton = true }) => {
  const cardRef = useRef(null);
  const downloadCardRef = useRef(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [cardKey, setCardKey] = useState(Date.now());

  useEffect(() => {
    setCardKey(Date.now());
  }, [member?.member_id, member?.name, member?.photo_url]);

  const downloadCard = async () => {
    if (!downloadCardRef.current) return;
    
    setIsDownloading(true);
    try {
      // Wait for render
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const canvas = await html2canvas(downloadCardRef.current, {
        scale: 3,
        backgroundColor: '#0a0a0f',
        useCORS: false,
        allowTaint: true,
        logging: false,
        imageTimeout: 5000,
        width: 420,
        windowWidth: 420
      });
      
      const link = document.createElement('a');
      const timestamp = new Date().toISOString().split('T')[0];
      link.download = `Achraj_AI_${member.member_id}_${timestamp}.png`;
      link.href = canvas.toDataURL('image/png', 1.0);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error('Error generating ID card:', err);
      alert('Failed to download ID card. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Card content for DISPLAY (shows member photo)
  const displayCard = (
    <div 
      ref={cardRef}
      key={cardKey}
      style={{
        width: '420px',
        minWidth: '420px',
        maxWidth: '420px',
        margin: '0 auto',
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0a0a0f 100%)',
        borderRadius: '16px',
        overflow: 'hidden',
        fontFamily: "'Orbitron', 'Segoe UI', sans-serif",
        position: 'relative',
        boxShadow: '0 0 40px rgba(0, 245, 255, 0.2), 0 0 80px rgba(212, 175, 55, 0.1), inset 0 0 60px rgba(0, 245, 255, 0.05)',
        border: '1px solid rgba(0, 245, 255, 0.3)',
        boxSizing: 'border-box'
      }}
    >
      <CircuitPattern />
      <div style={{ height: '4px', background: 'linear-gradient(90deg, #00F5FF 0%, #D4AF37 25%, #FF00FF 50%, #D4AF37 75%, #00F5FF 100%)', boxShadow: '0 0 20px rgba(0, 245, 255, 0.8)' }} />
      
      {/* Header */}
      <div style={{ background: 'linear-gradient(180deg, rgba(0, 245, 255, 0.15) 0%, transparent 100%)', padding: '16px 20px', position: 'relative', borderBottom: '1px solid rgba(0, 245, 255, 0.2)' }}>
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '2px', background: 'linear-gradient(90deg, transparent, #00F5FF, transparent)', opacity: 0.6 }} />
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.2), rgba(212, 175, 55, 0.1))', border: '1px solid #00F5FF', borderRadius: '8px', padding: '6px 12px', boxShadow: '0 0 15px rgba(0, 245, 255, 0.3)' }}>
            <div style={{ fontSize: '8px', color: '#00F5FF', letterSpacing: '3px', fontWeight: '700', textShadow: '0 0 10px #00F5FF' }}>AI VERIFIED</div>
            <div style={{ fontSize: '6px', color: '#D4AF37', letterSpacing: '2px', marginTop: '2px' }}>MEMBER ID</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '900', background: 'linear-gradient(90deg, #00F5FF, #D4AF37, #00F5FF)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '4px' }}>ACHRAJ</div>
            <div style={{ fontSize: '9px', color: '#D4AF37', letterSpacing: '6px', marginTop: '2px' }}>SERVICES PVT LTD</div>
          </div>
          <div style={{ background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.2), rgba(0, 245, 255, 0.1))', border: '1px solid #D4AF37', borderRadius: '8px', padding: '6px 12px', boxShadow: '0 0 15px rgba(212, 175, 55, 0.3)' }}>
            <div style={{ fontSize: '8px', color: '#D4AF37', letterSpacing: '3px', fontWeight: '700', textShadow: '0 0 10px #D4AF37' }}>NEURAL</div>
            <div style={{ fontSize: '6px', color: '#00F5FF', letterSpacing: '2px', marginTop: '2px' }}>NETWORK</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '20px', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
          {/* Logo */}
          <div style={{ position: 'relative' }}>
            <div style={{ width: '85px', height: '85px', borderRadius: '50%', background: 'conic-gradient(from 0deg, #00F5FF, #D4AF37, #FF00FF, #00F5FF)', padding: '3px', boxShadow: '0 0 30px rgba(0, 245, 255, 0.5)' }}>
              <div style={{ width: '79px', height: '79px', borderRadius: '50%', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                <img src={ACHRAJ_LOGO} alt="Achraj" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%', transform: 'scale(1.15)' }} />
              </div>
            </div>
            <div style={{ position: 'absolute', top: '-5px', left: '-5px', right: '-5px', bottom: '-5px', borderRadius: '50%', border: '1px solid rgba(0, 245, 255, 0.3)' }} />
          </div>

          {/* Photo - DISPLAY VERSION with actual photo */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-3px', left: '-3px', width: '20px', height: '20px', borderTop: '3px solid #00F5FF', borderLeft: '3px solid #00F5FF' }} />
            <div style={{ position: 'absolute', top: '-3px', right: '-3px', width: '20px', height: '20px', borderTop: '3px solid #00F5FF', borderRight: '3px solid #00F5FF' }} />
            <div style={{ position: 'absolute', bottom: '-3px', left: '-3px', width: '20px', height: '20px', borderBottom: '3px solid #D4AF37', borderLeft: '3px solid #D4AF37' }} />
            <div style={{ position: 'absolute', bottom: '-3px', right: '-3px', width: '20px', height: '20px', borderBottom: '3px solid #D4AF37', borderRight: '3px solid #D4AF37' }} />
            <div style={{ width: '105px', height: '125px', background: 'linear-gradient(180deg, rgba(0, 245, 255, 0.1), rgba(10, 10, 15, 0.9))', border: '1px solid rgba(0, 245, 255, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
              {member.photo_url ? (
                <img src={member.photo_url} alt={member.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }} />
              ) : null}
              <div style={{ fontSize: '48px', fontWeight: '900', background: 'linear-gradient(180deg, #00F5FF, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', display: member.photo_url ? 'none' : 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%' }}>
                {(member.name || 'M').charAt(0).toUpperCase()}
              </div>
            </div>
          </div>

          {/* Member ID */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.15), rgba(212, 175, 55, 0.1))', border: '1px solid rgba(0, 245, 255, 0.5)', borderRadius: '10px', padding: '12px 16px', boxShadow: '0 0 25px rgba(0, 245, 255, 0.3)' }}>
              <div style={{ fontSize: '7px', color: '#00F5FF', letterSpacing: '3px', marginBottom: '4px' }}>MEMBER ID</div>
              <div style={{ fontSize: '18px', fontWeight: '900', color: '#fff', letterSpacing: '2px' }}>{member.member_id}</div>
            </div>
            <DataStream count={18} />
          </div>
        </div>

        {/* Name */}
        <div style={{ textAlign: 'center', marginBottom: '18px', padding: '14px 0', background: 'linear-gradient(90deg, transparent, rgba(0, 245, 255, 0.1), transparent)', borderTop: '1px solid rgba(0, 245, 255, 0.3)', borderBottom: '1px solid rgba(0, 245, 255, 0.3)', position: 'relative' }}>
          <div style={{ position: 'absolute', top: '0', left: '10%', width: '80%', height: '1px', background: 'linear-gradient(90deg, transparent, #00F5FF, transparent)' }} />
          <h3 style={{ fontSize: '22px', fontWeight: '900', background: 'linear-gradient(90deg, #00F5FF 0%, #D4AF37 50%, #00F5FF 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '4px' }}>{member.name || 'MEMBER NAME'}</h3>
          <p style={{ fontSize: '10px', color: '#888', margin: 0, letterSpacing: '2px' }}>SPONSOR: <span style={{ color: '#D4AF37', fontWeight: '700' }}>{member.sponsor_id || 'COMPANY'}</span></p>
        </div>

        {/* Details */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px' }}>
          <div style={{ flex: 1, background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.1), rgba(0, 0, 0, 0.5))', border: '1px solid rgba(0, 245, 255, 0.3)', borderRadius: '8px', padding: '10px 12px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '3px', height: '100%', background: 'linear-gradient(180deg, #00F5FF, transparent)' }} />
            <p style={{ margin: 0, fontSize: '8px', color: '#00F5FF', letterSpacing: '2px', fontWeight: '700' }}>EMAIL</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '9px', color: '#ccc', wordBreak: 'break-all' }}>{member.email || 'N/A'}</p>
          </div>
          <div style={{ flex: 1, background: 'linear-gradient(135deg, rgba(212, 175, 55, 0.1), rgba(0, 0, 0, 0.5))', border: '1px solid rgba(212, 175, 55, 0.3)', borderRadius: '8px', padding: '10px 12px', position: 'relative' }}>
            <div style={{ position: 'absolute', top: 0, left: 0, width: '3px', height: '100%', background: 'linear-gradient(180deg, #D4AF37, transparent)' }} />
            <p style={{ margin: 0, fontSize: '8px', color: '#D4AF37', letterSpacing: '2px', fontWeight: '700' }}>PHONE</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#fff', fontWeight: '600', letterSpacing: '1px' }}>{maskPhoneNumber(member.phone)}</p>
          </div>
          <div style={{ flex: '0 0 70px', background: 'conic-gradient(from 0deg at 50% 50%, rgba(0, 245, 255, 0.3), rgba(212, 175, 55, 0.3), rgba(255, 0, 255, 0.2), rgba(0, 245, 255, 0.3))', border: '1px solid rgba(0, 245, 255, 0.5)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '8px', color: '#00F5FF', letterSpacing: '2px', fontWeight: '700' }}>PLAN</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '26px', fontWeight: '900', background: 'linear-gradient(180deg, #00F5FF, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{member.plan_type || 'C'}</p>
          </div>
        </div>

        {/* Barcode */}
        <div style={{ background: 'linear-gradient(180deg, rgba(255, 255, 255, 0.95), rgba(240, 240, 240, 0.95))', borderRadius: '8px', padding: '12px 16px', position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1px', marginBottom: '8px' }}>
            {Array.from({ length: 55 }).map((_, i) => {
              const code = (member.member_id || '').replace(/[^0-9]/g, '');
              const width = (parseInt(code[i % code.length] || '5') % 3) + 1;
              const isAccent = i % 7 === 0;
              return <div key={i} style={{ width: `${width}px`, height: '35px', backgroundColor: isAccent ? '#00F5FF' : (i % 2 === 0 ? '#0a0a0f' : '#D4AF37') }} />;
            })}
          </div>
          <p style={{ textAlign: 'center', margin: 0, fontSize: '12px', color: '#0a0a0f', fontFamily: "'Courier New', monospace", letterSpacing: '4px', fontWeight: '700' }}>{member.member_id}</p>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: 'linear-gradient(90deg, #0a0a0f, #1a1a2e, #0a0a0f)', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(0, 245, 255, 0.3)', position: 'relative' }}>
        <div style={{ position: 'absolute', top: 0, left: '20%', width: '60%', height: '1px', background: 'linear-gradient(90deg, transparent, #00F5FF, #D4AF37, #00F5FF, transparent)' }} />
        <span style={{ fontSize: '8px', color: '#D4AF37', letterSpacing: '1px', fontStyle: 'italic' }}>जुडकर खर्च नहीं, आय करें</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '4px', height: '4px', background: '#00F5FF', borderRadius: '50%' }} />
          <span style={{ fontSize: '10px', fontWeight: '900', background: 'linear-gradient(90deg, #00F5FF, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text', letterSpacing: '3px' }}>ACHRAJ AI</span>
          <div style={{ width: '4px', height: '4px', background: '#D4AF37', borderRadius: '50%' }} />
        </div>
        <span style={{ fontSize: '8px', color: '#00F5FF', letterSpacing: '1px' }}>achrajai.com</span>
      </div>
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #00F5FF 0%, #D4AF37 25%, #FF00FF 50%, #D4AF37 75%, #00F5FF 100%)' }} />
    </div>
  );

  // Card content for DOWNLOAD (uses initial instead of photo to avoid CORS)
  const downloadCard_content = (
    <div 
      ref={downloadCardRef}
      style={{
        width: '420px',
        minWidth: '420px',
        maxWidth: '420px',
        position: 'absolute',
        left: '-9999px',
        top: 0,
        background: 'linear-gradient(135deg, #0a0a0f 0%, #1a1a2e 50%, #0a0a0f 100%)',
        borderRadius: '16px',
        overflow: 'hidden',
        fontFamily: "'Segoe UI', sans-serif",
        border: '1px solid rgba(0, 245, 255, 0.3)',
        boxSizing: 'border-box'
      }}
    >
      {/* Simplified circuit pattern as background color */}
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, background: 'radial-gradient(circle at 20% 20%, rgba(0, 245, 255, 0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(212, 175, 55, 0.05) 0%, transparent 50%)' }} />
      
      <div style={{ height: '4px', background: 'linear-gradient(90deg, #00F5FF 0%, #D4AF37 25%, #FF00FF 50%, #D4AF37 75%, #00F5FF 100%)' }} />
      
      {/* Header */}
      <div style={{ background: 'linear-gradient(180deg, rgba(0, 245, 255, 0.15) 0%, transparent 100%)', padding: '16px 20px', borderBottom: '1px solid rgba(0, 245, 255, 0.2)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ background: 'rgba(0, 245, 255, 0.15)', border: '1px solid #00F5FF', borderRadius: '8px', padding: '6px 12px' }}>
            <div style={{ fontSize: '8px', color: '#00F5FF', letterSpacing: '3px', fontWeight: '700' }}>AI VERIFIED</div>
            <div style={{ fontSize: '6px', color: '#D4AF37', letterSpacing: '2px', marginTop: '2px' }}>MEMBER ID</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '16px', fontWeight: '900', color: '#00F5FF', letterSpacing: '4px' }}>ACHRAJ</div>
            <div style={{ fontSize: '9px', color: '#D4AF37', letterSpacing: '6px', marginTop: '2px' }}>SERVICES PVT LTD</div>
          </div>
          <div style={{ background: 'rgba(212, 175, 55, 0.15)', border: '1px solid #D4AF37', borderRadius: '8px', padding: '6px 12px' }}>
            <div style={{ fontSize: '8px', color: '#D4AF37', letterSpacing: '3px', fontWeight: '700' }}>NEURAL</div>
            <div style={{ fontSize: '6px', color: '#00F5FF', letterSpacing: '2px', marginTop: '2px' }}>NETWORK</div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ padding: '20px', position: 'relative', zIndex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '18px' }}>
          {/* Logo - use text instead of image for download */}
          <div style={{ width: '85px', height: '85px', borderRadius: '50%', background: 'linear-gradient(135deg, #00F5FF, #D4AF37)', padding: '3px' }}>
            <div style={{ width: '79px', height: '79px', borderRadius: '50%', background: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              <div style={{ fontSize: '24px', fontWeight: '900', color: '#D4AF37', letterSpacing: '2px' }}>A</div>
              <div style={{ fontSize: '6px', color: '#1a1a2e', letterSpacing: '1px', fontWeight: '700' }}>ACHRAJ</div>
            </div>
          </div>

          {/* Photo - ALWAYS show initial for download */}
          <div style={{ position: 'relative' }}>
            <div style={{ position: 'absolute', top: '-3px', left: '-3px', width: '20px', height: '20px', borderTop: '3px solid #00F5FF', borderLeft: '3px solid #00F5FF' }} />
            <div style={{ position: 'absolute', top: '-3px', right: '-3px', width: '20px', height: '20px', borderTop: '3px solid #00F5FF', borderRight: '3px solid #00F5FF' }} />
            <div style={{ position: 'absolute', bottom: '-3px', left: '-3px', width: '20px', height: '20px', borderBottom: '3px solid #D4AF37', borderLeft: '3px solid #D4AF37' }} />
            <div style={{ position: 'absolute', bottom: '-3px', right: '-3px', width: '20px', height: '20px', borderBottom: '3px solid #D4AF37', borderRight: '3px solid #D4AF37' }} />
            <div style={{ width: '105px', height: '125px', background: 'linear-gradient(180deg, rgba(0, 245, 255, 0.2), rgba(10, 10, 15, 0.9))', border: '1px solid rgba(0, 245, 255, 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <div style={{ fontSize: '48px', fontWeight: '900', color: '#00F5FF' }}>
                {(member.name || 'M').charAt(0).toUpperCase()}
              </div>
            </div>
          </div>

          {/* Member ID */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'flex-end' }}>
            <div style={{ background: 'rgba(0, 245, 255, 0.1)', border: '1px solid rgba(0, 245, 255, 0.5)', borderRadius: '10px', padding: '12px 16px' }}>
              <div style={{ fontSize: '7px', color: '#00F5FF', letterSpacing: '3px', marginBottom: '4px' }}>MEMBER ID</div>
              <div style={{ fontSize: '18px', fontWeight: '900', color: '#fff', letterSpacing: '2px' }}>{member.member_id}</div>
            </div>
            {/* Static bars instead of random */}
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '2px', height: '25px' }}>
              {[12,18,8,20,14,10,22,16,12,18,8,14,20,10,16,22,12,18].map((h, i) => (
                <div key={i} style={{ width: '3px', height: `${h}px`, background: i % 2 === 0 ? '#00F5FF' : '#D4AF37', borderRadius: '1px' }} />
              ))}
            </div>
          </div>
        </div>

        {/* Name */}
        <div style={{ textAlign: 'center', marginBottom: '18px', padding: '14px 0', background: 'rgba(0, 245, 255, 0.05)', borderTop: '1px solid rgba(0, 245, 255, 0.3)', borderBottom: '1px solid rgba(0, 245, 255, 0.3)' }}>
          <h3 style={{ fontSize: '22px', fontWeight: '900', color: '#00F5FF', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '4px' }}>{member.name || 'MEMBER NAME'}</h3>
          <p style={{ fontSize: '10px', color: '#888', margin: 0, letterSpacing: '2px' }}>SPONSOR: <span style={{ color: '#D4AF37', fontWeight: '700' }}>{member.sponsor_id || 'COMPANY'}</span></p>
        </div>

        {/* Details */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '18px' }}>
          <div style={{ flex: 1, background: 'rgba(0, 245, 255, 0.1)', border: '1px solid rgba(0, 245, 255, 0.3)', borderRadius: '8px', padding: '10px 12px' }}>
            <p style={{ margin: 0, fontSize: '8px', color: '#00F5FF', letterSpacing: '2px', fontWeight: '700' }}>EMAIL</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '9px', color: '#ccc', wordBreak: 'break-all' }}>{member.email || 'N/A'}</p>
          </div>
          <div style={{ flex: 1, background: 'rgba(212, 175, 55, 0.1)', border: '1px solid rgba(212, 175, 55, 0.3)', borderRadius: '8px', padding: '10px 12px' }}>
            <p style={{ margin: 0, fontSize: '8px', color: '#D4AF37', letterSpacing: '2px', fontWeight: '700' }}>PHONE</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#fff', fontWeight: '600', letterSpacing: '1px' }}>{maskPhoneNumber(member.phone)}</p>
          </div>
          <div style={{ flex: '0 0 70px', background: 'linear-gradient(135deg, rgba(0, 245, 255, 0.2), rgba(212, 175, 55, 0.2))', border: '1px solid rgba(0, 245, 255, 0.5)', borderRadius: '8px', padding: '10px', textAlign: 'center' }}>
            <p style={{ margin: 0, fontSize: '8px', color: '#00F5FF', letterSpacing: '2px', fontWeight: '700' }}>PLAN</p>
            <p style={{ margin: '4px 0 0 0', fontSize: '26px', fontWeight: '900', color: '#D4AF37' }}>{member.plan_type || 'C'}</p>
          </div>
        </div>

        {/* Barcode */}
        <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '12px 16px' }}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '1px', marginBottom: '8px' }}>
            {Array.from({ length: 55 }).map((_, i) => {
              const code = (member.member_id || '').replace(/[^0-9]/g, '');
              const width = (parseInt(code[i % code.length] || '5') % 3) + 1;
              const isAccent = i % 7 === 0;
              return <div key={i} style={{ width: `${width}px`, height: '35px', backgroundColor: isAccent ? '#00F5FF' : (i % 2 === 0 ? '#0a0a0f' : '#D4AF37') }} />;
            })}
          </div>
          <p style={{ textAlign: 'center', margin: 0, fontSize: '12px', color: '#0a0a0f', fontFamily: "'Courier New', monospace", letterSpacing: '4px', fontWeight: '700' }}>{member.member_id}</p>
        </div>
      </div>

      {/* Footer */}
      <div style={{ background: '#0a0a0f', padding: '12px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid rgba(0, 245, 255, 0.3)' }}>
        <span style={{ fontSize: '8px', color: '#D4AF37', letterSpacing: '1px', fontStyle: 'italic' }}>जुडकर खर्च नहीं, आय करें</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
          <div style={{ width: '4px', height: '4px', background: '#00F5FF', borderRadius: '50%' }} />
          <span style={{ fontSize: '10px', fontWeight: '900', color: '#D4AF37', letterSpacing: '3px' }}>ACHRAJ AI</span>
          <div style={{ width: '4px', height: '4px', background: '#D4AF37', borderRadius: '50%' }} />
        </div>
        <span style={{ fontSize: '8px', color: '#00F5FF', letterSpacing: '1px' }}>achrajai.com</span>
      </div>
      <div style={{ height: '3px', background: 'linear-gradient(90deg, #00F5FF, #D4AF37, #FF00FF, #D4AF37, #00F5FF)' }} />
    </div>
  );

  if (showCloseButton) {
    return (
      <div className="fixed inset-0 bg-black/95 flex items-center justify-center p-4" style={{ zIndex: 9999 }}>
        <div className="bg-gradient-to-b from-[#0a0a0f] to-[#1a1a2e] rounded-2xl p-6 max-w-xl w-full max-h-[95vh] overflow-y-auto border border-[#00F5FF]/30 shadow-2xl">
          <h2 className="text-xl font-bold text-center tracking-wider mb-4" style={{ background: 'linear-gradient(90deg, #00F5FF, #D4AF37)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>AI MEMBER IDENTITY</h2>
          
          {displayCard}
          {downloadCard_content}

          <div className="flex gap-4 mt-6 justify-center">
            <Button 
              onClick={downloadCard}
              disabled={isDownloading}
              className="font-bold px-8 py-3 rounded-full disabled:opacity-50"
              style={{ background: 'linear-gradient(90deg, #00F5FF, #D4AF37)', color: '#0a0a0f' }}
            >
              {isDownloading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</> : <><Download className="w-5 h-5 mr-2" /> Download ID Card</>}
            </Button>
            {onClose && (
              <Button onClick={onClose} variant="outline" className="px-8 py-3 rounded-full" style={{ borderColor: 'rgba(0, 245, 255, 0.5)', color: '#00F5FF' }}>Close</Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center">
      {displayCard}
      {downloadCard_content}
      <Button 
        onClick={downloadCard}
        disabled={isDownloading}
        className="font-bold px-8 py-3 rounded-full mt-6 disabled:opacity-50"
        style={{ background: 'linear-gradient(90deg, #00F5FF, #D4AF37)', color: '#0a0a0f' }}
      >
        {isDownloading ? <><Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...</> : <><Download className="w-5 h-5 mr-2" /> Download ID Card</>}
      </Button>
    </div>
  );
};

export default MemberIDCard;
